// Režim sady „dárek ZDARMA" — výběr produktů, ze kterých si zákazník dárek
// vybírá (např. čepice nebo nákrčník). Pořadí přetažením a vlastní název ve
// widgetu stejně jako u komponent (ProductSetItemsList).

import { useState } from 'react';
import { ProductSetItemsList, type SelectedItem } from './ProductSetItemsList';
import { type Product, useProductSearch } from './productSetSearch';

type Props = {
  eshopId: string;
  gifts: SelectedItem[];
  onChange: (gifts: SelectedItem[]) => void;
  extraPct: number;
  onExtraPct: (pct: number) => void;
  inputCls: string;
};

export function ProductSetGiftsSection({
  eshopId,
  gifts,
  onChange,
  extraPct,
  onExtraPct,
  inputCls,
}: Props) {
  const search = useProductSearch(eshopId);
  const [open, setOpen] = useState(false);

  function add(p: Product) {
    if (gifts.some((g) => g.shopitem_id === p.shopitem_id)) return;
    onChange([
      ...gifts,
      {
        shopitem_id: p.shopitem_id,
        parent_code: p.parent_code,
        name: p.name,
        image_url: p.image_url,
        item_id: null,
        display_name: '',
        original_display_name: '',
        original_order: null,
      },
    ]);
    search.setQuery('');
    setOpen(false);
  }

  return (
    <div className="relative">
      <span className="text-sm font-medium text-text-secondary block mb-1.5">
        Dárky na výběr <span className="text-danger">*</span>
      </span>
      <input
        type="text"
        value={search.query}
        onChange={(e) => {
          search.setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Přidat dárek — hledat podle kódu nebo názvu…"
        className={inputCls}
      />
      {open && search.query.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-72 overflow-y-auto z-10">
          {search.searching && <div className="px-3 py-2 text-sm text-text-muted">Hledám…</div>}
          {!search.searching && search.results.length === 0 && (
            <div className="px-3 py-2 text-sm text-text-muted">Žádné výsledky.</div>
          )}
          {!search.searching &&
            search.results.map((p) => {
              const already = gifts.some((g) => g.shopitem_id === p.shopitem_id);
              return (
                <button
                  key={p.shopitem_id}
                  type="button"
                  onClick={() => !already && add(p)}
                  disabled={already}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-8 h-8 rounded object-cover bg-bg flex-shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-bg border border-border flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text truncate">{p.name}</div>
                    <div className="text-xs text-text-muted font-mono">
                      {p.parent_code ?? '—'} · #{p.shopitem_id}
                    </div>
                  </div>
                  {already && <span className="text-xs text-text-muted">už přidán</span>}
                </button>
              );
            })}
        </div>
      )}
      {gifts.length > 0 && <ProductSetItemsList items={gifts} onChange={onChange} />}
      <p className="text-xs text-text-muted mt-1">
        Zákazník si za každou kompletní sadu vybere jeden dárek (a jeho velikost). Dárek se přidá do
        košíku a jeho cenu odečte kód SADA-…. Krátký název ve widgetu (např. „Čepice") se hodí do
        souhrnu.
      </p>
      {gifts.length >= 2 && (
        <div className="mt-3">
          <label
            className="text-sm font-medium text-text-secondary block mb-1.5"
            htmlFor="darek-dalsi-sleva"
          >
            Sleva na další dárek (%)
          </label>
          <input
            id="darek-dalsi-sleva"
            type="number"
            min={0}
            max={100}
            step={1}
            value={extraPct}
            onChange={(e) => onExtraPct(Number(e.target.value))}
            className={`${inputCls} max-w-[140px]`}
          />
          <p className="text-xs text-text-muted mt-1">
            Kdo chce i jiný dárek než ten zdarma, dostane ho s touto slevou (ve widgetu krok
            „Přidejte i druhý dárek").
          </p>
        </div>
      )}
    </div>
  );
}
